import React, { createContext, useContext, useMemo, useState } from "react";
import PeriodPicker from "./PeriodPicker";
import ResourceInsightsDrawer from "./ResourceInsightsDrawer";
import { useInsightsPeriod } from "./periodStore";

const ResourceInsightsContext = createContext(null);

/**
 * Hosts Resource Insights for every page of the application that has no period of its own. Anything below it can
 * open the panel with `useResourceInsights()`; the panel carries a period picker, and the period it sets is shared by
 * all those pages. The planner does not use this host: it opens Resource Insights over its own period.
 */
export function ResourceInsightsProvider({ children }) {
  // What was asked for: `{ resource, projectFilter, onOpenTask, onEditTask }`, or nothing while the panel is closed.
  const [request, setRequest] = useState(null);
  const { range } = useInsightsPeriod();

  const actions = useMemo(
    () => ({
      openResourceInsights: (resource, options = {}) => setRequest({ resource, ...options }),
      closeResourceInsights: () => setRequest(null),
    }),
    []
  );

  return (
    <ResourceInsightsContext.Provider value={actions}>
      {children}
      <ResourceInsightsDrawer
        open={Boolean(request)}
        resource={request?.resource ?? null}
        period={range}
        periodControl={<PeriodPicker />}
        projectFilter={request?.projectFilter ?? null}
        onNavigateToTask={request?.onOpenTask}
        onEditTask={request?.onEditTask}
        onClose={actions.closeResourceInsights}
      />
    </ResourceInsightsContext.Provider>
  );
}

/**
 * Opens and closes Resource Insights from any page below `ResourceInsightsProvider`.
 *
 * `openResourceInsights(resource, options?)` takes who it is about, `{ id, name, image, title, email, phoneNumber,
 * isExternal }` (only `id` is required), and optionally `projectFilter` (`{ id, name }`, to limit it to a project),
 * `onOpenTask(task)` (what pressing a task does) and `onEditTask(task)` (what its pencil does). Without the two
 * handlers the tasks are listed but cannot be pressed.
 */
export function useResourceInsights() {
  const context = useContext(ResourceInsightsContext);
  if (!context) throw new Error("useResourceInsights has to be used below a ResourceInsightsProvider.");
  return context;
}
